import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { HandCoins, Hourglass, Receipt, Sparkles } from "lucide-react";
import {
  COMMISSION_ROUTES,
  ORDER_ROUTES,
  commissionSummaryResponseSchema,
  formatPrice,
  myPointsResponseSchema,
} from "@nh/shared";
import { authFetch } from "@/shared/api/session";
import { KpiCard } from "@/features/admin/components";
import { useMyOrders } from "@/features/mi-negocio/minegocio.api";
import { otherBalancesHint } from "./otherBalances";

/**
 * Los cuatro números que tiene TODA persona con cuenta: sus pedidos, sus
 * puntos, lo que tiene por liberar y lo que ya cobró.
 *
 * ── Por qué es un componente y no cuatro `KpiCard` copiadas ──────────
 * Anahí (1-sep): «si pasa del rol de socio a franquiciatario debe también
 * mostrarse ya la información que agregó como socio ahora como
 * franquiciatario».
 *
 * Estos cuatro vivían escritos a mano DENTRO del resumen del socio, así
 * que al adquirir la franquicia y aterrizar en `/mi-negocio` desaparecían
 * de la vista: sus pedidos, sus puntos y sus comisiones seguían ahí en la
 * base, pero el panel nuevo solo hablaba de la tienda. Quien acaba de
 * pagar una franquicia veía MENOS que el día anterior.
 *
 * Volverlos a copiar en el otro panel habría arreglado hoy y roto mañana:
 * dos copias del mismo número se separan en el primer cambio que toque
 * una sola. Como componente, los dos resúmenes enseñan lo mismo porque es
 * literalmente lo mismo.
 *
 * Ser franquiciatario NO deja de ser socio: es serlo y además tener
 * tienda. Los números de la tienda se SUMAN a estos, no los sustituyen.
 */
export function MemberKpis() {
  const { t } = useTranslation();

  // Los pedidos que HIZO, no los que vendió: `role: "buyer"`. En el panel
  // del franquiciatario conviven con "Ventas", que es el otro lado.
  const orders = useMyOrders({ page: 1, perPage: 1, role: "buyer" });

  const summary = useQuery({
    queryKey: ["me", "commissions", "summary"],
    queryFn: async () => {
      const data = await authFetch(COMMISSION_ROUTES.summary, commissionSummaryResponseSchema, {
        baseUrl: "",
      });
      return data.summary;
    },
  });

  /* Los PUNTOS: la foto de PV/CV de sus compras validadas. */
  const points = useQuery({
    queryKey: ["me", "points"],
    queryFn: () => authFetch(ORDER_ROUTES.minePoints, myPointsResponseSchema, { baseUrl: "" }),
  });

  return (
    <>
      <KpiCard icon={Receipt} item="mis-pedidos" label={t("Mis pedidos")} value={String(orders.data?.total ?? "—")} />
      <KpiCard
        icon={Sparkles}
        item="puntos"
        label={t("Mis puntos (PV)")}
        value={String(points.data?.volumePoints ?? "—")}
        hint={t("{{cv}} CV · {{n}} compras", {
          cv: points.data?.commissionPoints ?? 0,
          n: points.data?.purchases ?? 0,
        })}
      />
      <KpiCard
        icon={Hourglass}
        item="por-liberar"
        label={t("Por liberar")}
        value={summary.data ? formatPrice(summary.data.pendingCents, summary.data.currency) : "—"}
        hint={otherBalancesHint(summary.data, "pendingCents")}
      />
      <KpiCard
        icon={HandCoins}
        item="pagado"
        label={t("Pagado")}
        value={summary.data ? formatPrice(summary.data.paidCents, summary.data.currency) : "—"}
        hint={otherBalancesHint(summary.data, "paidCents")}
      />
    </>
  );
}
